import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Phone, MessageCircle, MapPin, Star, ExternalLink,
  Calculator, Landmark, ShieldCheck, Building2, Briefcase, FileCheck2, LineChart, Globe2,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, SectionHeading, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { useConsultation } from "../context/ConsultationContext";
import { FIRM, FOUNDER, TEL_LINK, waLink, WA_MESSAGES } from "../data/firm";
import { CATEGORIES } from "../data/services";
import { INDUSTRIES } from "../data/industries";
import { TEAM, initials } from "../data/team";
import { PORTALS, TAX_CALCULATOR_DISCLAIMER } from "../data/portals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG =
  "https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBnbGFzcyUyMGJ1aWxkaW5nfGVufDB8fHx8MTc4NjYwOTY4Nnww&ixlib=rb-4.1.0&q=85";

const CATEGORY_ICONS = {
  "direct-taxation": Landmark,
  "gst-indirect-taxation": FileCheck2,
  "audit-assurance": ShieldCheck,
  "accounting-finance": LineChart,
  "business-startup-advisory": Briefcase,
  "corporate-regulatory-compliance": Building2,
  "specialised-advisory": Globe2,
  "registrations-licences": FileCheck2,
};

const KineticLine = ({ children, delay, className }) => {
  const reduce = useReducedMotion();
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={reduce ? false : { y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const { openConsultation } = useConsultation();
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 120]);
  const fade = useTransform(scrollY, [0, 420], [1, 0.25]);

  return (
    <section className="relative overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="h-[115%] w-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto max-w-7xl px-5 pb-24 pt-20 sm:pt-28 lg:px-8 lg:pb-32">
        <KineticLine delay={0.1}>
          <span className="inline-flex items-center gap-3 border border-white/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full bg-corpblue-500" /> Est. {FIRM.established} · Vashi, Navi Mumbai
          </span>
        </KineticLine>

        <h1 className="mt-8 font-display font-semibold leading-[0.98] text-white">
          <KineticLine delay={0.25} className="text-4xl sm:text-6xl lg:text-7xl">
            Aniruddha Sonar
          </KineticLine>
          <KineticLine delay={0.4} className="text-4xl sm:text-6xl lg:text-7xl">
            <span className="italic text-corpblue-500">&amp;</span> Associates
          </KineticLine>
          <KineticLine delay={0.55} className="mt-5">
            <span className="text-sm font-sans font-bold uppercase tracking-[0.42em] text-slate-300 sm:text-base">
              Chartered Accountants
            </span>
          </KineticLine>
        </h1>

        <KineticLine delay={0.75}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Professional taxation, audit, compliance and business advisory services - delivered with a
            technology-enabled, detail-first approach.
          </p>
        </KineticLine>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => openConsultation()}
            data-testid="hero-get-consultation-button"
            className="group inline-flex items-center gap-2 bg-corpblue px-7 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-navy"
          >
            Get Consultation
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <Link
            to="/contact"
            data-testid="hero-contact-button"
            className="inline-flex items-center gap-2 border border-white/30 px-7 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-white hover:bg-white/10"
          >
            Contact Us
          </Link>
          <div className="flex items-center gap-2">
            <a
              href={TEL_LINK}
              data-testid="hero-call-button"
              aria-label="Call now"
              className="flex h-12 w-12 items-center justify-center border border-white/30 text-white transition-colors hover:border-corpblue-500 hover:text-corpblue-500"
            >
              <Phone className="h-5 w-5" />
            </a>
            <a
              href={waLink(WA_MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              aria-label="Chat on WhatsApp"
              className="flex h-12 w-12 items-center justify-center border border-white/30 text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]"
            >
              <MessageCircle className="h-5 w-5" />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const AboutFirm = () => (
  <section className="py-20 lg:py-28" data-testid="home-about-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="About the Firm"
            title="A technology-enabled Chartered Accountancy practice."
          />
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-slate-600 md:text-lg">
              {FIRM.name} is a Chartered Accountancy firm established in {FIRM.established}, based at Vashi, Navi
              Mumbai. The firm serves individuals, startups, SMEs and corporates across taxation, GST, audit,
              accounting, compliance and business advisory.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-600 md:text-lg">
              Led by {FOUNDER.name} with a professional team of five, the practice combines disciplined compliance
              processes with modern tools - including AI-assisted workflows - to deliver accurate, timely and
              well-documented work.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-px bg-slate-200">
              {[
                [FIRM.established, "Established"],
                ["5", "Professionals"],
                ["8", "Service Verticals"],
              ].map(([num, label]) => (
                <div key={label} className="bg-white p-6" data-testid={`firm-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="font-display text-4xl font-semibold text-navy">{num}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-readmore"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-corpblue"
            >
              More about the firm
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const FounderSpotlight = () => (
  <section className="bg-slate-50 py-20 lg:py-28" data-testid="home-founder-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-corpblue/30" aria-hidden="true" />
            <img
              src={FOUNDER.photo}
              alt={`${FOUNDER.name}, ${FOUNDER.role}, ${FIRM.name}`}
              className="relative w-full object-cover grayscale-[15%] transition-all duration-500 hover:grayscale-0"
              loading="lazy"
              data-testid="founder-photo"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Meet the Founder</Eyebrow>
            <h2 className="font-display text-4xl font-semibold leading-tight text-navy sm:text-5xl">
              {FOUNDER.name}
            </h2>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.22em] text-corpblue">
              {FOUNDER.role} · {FOUNDER.qualifications}
            </p>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
              Aniruddha Sonar qualified as a Chartered Accountant in {FOUNDER.membershipYear} and founded the firm
              the same year. With approximately three years of professional experience, he leads a practice focused
              on direct and indirect taxation, audit and assurance, startup and business advisory, and specialised
              areas including NRI taxation, FEMA and RERA compliance - with an emphasis on AI-driven, technology-first
              ways of working.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {FOUNDER.focus.slice(0, 6).map((f) => (
                <span key={f} className="border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-navy-700">
                  {f}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to="/about#founder"
                data-testid="founder-readmore"
                className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-corpblue"
              >
                Read the full profile
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={FOUNDER.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-corpblue"
              >
                LinkedIn <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const CoreServices = () => (
  <section className="py-20 lg:py-28" data-testid="home-services-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <SectionHeading
          eyebrow="Core Services"
          title="Eight verticals. One accountable team."
          intro="From routine compliance to specialised cross-border advisory, each engagement is scoped clearly and delivered with documented rigour."
        />
        <Reveal delay={0.15}>
          <Link
            to="/services"
            data-testid="home-services-viewall"
            className="group inline-flex shrink-0 items-center gap-2 border border-navy px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-navy transition-colors hover:bg-navy hover:text-white"
          >
            All Services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {CATEGORIES.map((cat, i) => {
          const Icon = CATEGORY_ICONS[cat.slug] || FileCheck2;
          return (
            <Reveal key={cat.slug} delay={(i % 4) * 0.07}>
              <Link
                to={`/services#${cat.slug}`}
                data-testid={`service-category-${cat.slug}`}
                className="group flex h-full flex-col bg-white p-7 transition-colors hover:bg-navy"
              >
                <Icon className="h-7 w-7 text-corpblue transition-colors group-hover:text-corpblue-500" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-2xl font-semibold leading-snug text-navy transition-colors group-hover:text-white">
                  {cat.title}
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">
                  {cat.intro}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-corpblue transition-colors group-hover:text-corpblue-500">
                  {cat.services.length} services <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-navy py-20 lg:py-28" data-testid="home-industries-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        dark
        eyebrow="Industries We Serve"
        title="Sector-aware advice, not generic answers."
        intro="Compliance and advisory shaped by how each industry actually operates."
      />
      <div className="mt-14 grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-3 lg:grid-cols-4">
        {INDUSTRIES.slice(0, 8).map((ind, i) => (
          <Reveal key={ind.slug} delay={(i % 4) * 0.06}>
            <Link
              to={`/industries#${ind.slug}`}
              data-testid={`home-industry-${ind.slug}`}
              className="group flex h-full items-center justify-between bg-navy p-6 transition-colors hover:bg-navy-800"
            >
              <span className="font-display text-lg font-medium text-slate-200 transition-colors group-hover:text-white md:text-xl">
                {ind.name}
              </span>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-500 transition-all group-hover:translate-x-0.5 group-hover:text-corpblue-500" />
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link
          to="/industries"
          data-testid="home-industries-viewall"
          className="group mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-corpblue-500"
        >
          View all 16 industries
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const TeamSection = () => (
  <section className="py-20 lg:py-28" data-testid="home-team-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Our Professional Team"
        title="Five professionals, one standard of work."
        intro="A compact, accountable team across taxation, accounts, audit and compliance."
      />
      <div className="mt-14 grid grid-cols-2 gap-px bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
        {TEAM.map((member, i) => (
          <Reveal key={member.id} delay={i * 0.06}>
            <div className="group h-full bg-white p-6 text-center" data-testid={`team-card-${member.id}`}>
              {member.photo ? (
                <img src={member.photo} alt={member.name} className="mx-auto h-24 w-24 rounded-full object-cover" loading="lazy" />
              ) : (
                <div
                  className="mx-auto flex h-24 w-24 items-center justify-center rounded-full border border-slate-200 bg-slate-50 font-display text-2xl font-semibold text-navy transition-colors group-hover:border-corpblue group-hover:text-corpblue"
                  aria-hidden="true"
                >
                  {initials(member.name)}
                </div>
              )}
              <h3 className="mt-4 font-display text-lg font-semibold leading-snug text-navy">{member.name}</h3>
              <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{member.role}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-corpblue">{member.qualifications}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const APPROACH = [
  {
    no: "01",
    title: "Understand before we advise",
    text: "Every engagement begins with understanding your business, records and objectives - advice follows facts, never templates.",
  },
  {
    no: "02",
    title: "Technology-enabled delivery",
    text: "AI-assisted workflows, structured checklists and digital documentation keep work accurate, traceable and on time.",
  },
  {
    no: "03",
    title: "Compliance without surprises",
    text: "Clear scope, defined timelines and proactive communication - you always know what is happening and what comes next.",
  },
  {
    no: "04",
    title: "Advice you can act on",
    text: "Findings and recommendations are explained in plain language, with the trade-offs made explicit.",
  },
];

const Approach = () => (
  <section className="bg-slate-50 py-20 lg:py-28" data-testid="home-approach-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading eyebrow="Our Professional Approach" title="How we work." />
      <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-2 lg:grid-cols-4">
        {APPROACH.map((step, i) => (
          <Reveal key={step.no} delay={i * 0.08}>
            <div className="h-full bg-white p-8" data-testid={`approach-step-${step.no}`}>
              <p className="font-display text-5xl font-semibold text-slate-200">{step.no}</p>
              <h3 className="mt-4 font-display text-xl font-semibold leading-snug text-navy">{step.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{step.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const InsightsPreview = () => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((res) => setArticles(res.data.slice(0, 3)))
      .catch(() => setArticles([]));
  }, []);

  if (articles.length === 0) return null;
  return (
    <section className="py-20 lg:py-28" data-testid="home-insights-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading eyebrow="Insights" title="Tax & business insights." />
          <Reveal delay={0.15}>
            <Link
              to="/insights"
              data-testid="home-insights-viewall"
              className="group inline-flex shrink-0 items-center gap-2 text-sm font-bold uppercase tracking-widest text-corpblue"
            >
              All articles <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-3">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`home-article-${a.slug}`}
                className="group flex h-full flex-col bg-white p-7 transition-colors hover:bg-slate-50"
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-corpblue">{a.category}</span>
                <h3 className="mt-3 flex-1 font-display text-2xl font-semibold leading-snug text-navy group-hover:text-corpblue">
                  {a.title}
                </h3>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-500">{a.excerpt}</p>
                <p className="mt-5 text-xs text-slate-400">
                  Published {new Date(a.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const GoogleReviews = () => (
  <section className="border-y border-slate-200 bg-white py-20 lg:py-24" data-testid="home-reviews-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Google Reviews</Eyebrow>
            <h2 className="font-display text-4xl font-semibold text-navy sm:text-5xl">
              What clients say, on Google.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              View our latest reviews and rating on our Google Business Profile. Reviews are hosted by Google and
              reflect the experiences clients choose to share there.
            </p>
            <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="border border-slate-200 bg-slate-50 p-8">
            <p className="text-sm leading-relaxed text-slate-600">
              Read current reviews, or share your own experience of working with the firm.
            </p>
            <div className="mt-6 space-y-3">
              <a
                href={FIRM.mapsShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-view-on-google"
                className="flex items-center justify-between border border-navy bg-navy px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-corpblue"
              >
                View all reviews on Google <ExternalLink className="h-4 w-4" />
              </a>
              <a
                href={FIRM.mapsDirectionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-get-directions"
                className="flex items-center justify-between border border-slate-300 px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-navy transition-colors hover:border-corpblue hover:text-corpblue"
              >
                Get directions <MapPin className="h-4 w-4" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Portals = () => (
  <section className="bg-slate-50 py-20 lg:py-28" data-testid="home-portals-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <SectionHeading
        eyebrow="Useful Government Portals"
        title="Official portals, one click away."
        intro="Direct links to the official government portals our clients use most. These portals are operated by their respective government authorities."
      />
      <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
        {PORTALS.map((portal, i) => (
          <Reveal key={portal.name} delay={(i % 3) * 0.06}>
            <a
              href={portal.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`portal-${portal.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex h-full flex-col bg-white p-7 transition-colors hover:bg-navy"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display text-xl font-semibold leading-snug text-navy transition-colors group-hover:text-white">
                  {portal.name}
                </h3>
                <ExternalLink className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-corpblue-500" />
              </div>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">
                {portal.description}
              </p>
              <span className="mt-4 inline-block w-max border border-slate-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 transition-colors group-hover:border-slate-600 group-hover:text-slate-400">
                External Government Portal
              </span>
            </a>
          </Reveal>
        ))}
        <Reveal delay={0.18}>
          <div className="flex h-full flex-col bg-navy p-7" data-testid="tax-calculator-card">
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-xl font-semibold leading-snug text-white">Income Tax Calculator</h3>
              <Calculator className="h-5 w-5 shrink-0 text-corpblue-500" />
            </div>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">
              Estimate your tax on the official Income Tax Department calculator.
            </p>
            <a
              href="https://www.incometax.gov.in/iec/foportal/income-tax-calculator"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tax-calculator-open-button"
              className="mt-5 inline-flex w-max items-center gap-2 bg-corpblue px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-navy"
            >
              Open official tax calculator <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <p className="mt-4 text-[11px] leading-relaxed text-slate-400">{TAX_CALCULATOR_DISCLAIMER}</p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ConsultationCTA = () => {
  const { openConsultation } = useConsultation();
  return (
    <section className="relative overflow-hidden bg-navy py-20 lg:py-24" data-testid="home-consultation-cta">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true">
        <p className="whitespace-nowrap font-display text-[22vw] font-bold leading-none text-white">
          Tax · Audit · Advisory
        </p>
      </div>
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <h2 className="font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
              Discuss your requirement with a Chartered Accountant.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300 md:text-lg">
              Share a few details and the firm will get back to you. Prefer to talk first? Call or message us on
              WhatsApp.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={() => openConsultation()}
                data-testid="cta-book-consultation-button"
                className="group inline-flex items-center gap-2 bg-corpblue px-7 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-navy"
              >
                Book Consultation
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink(WA_MESSAGES.consultation)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-button"
                className="inline-flex items-center gap-2 border border-white/30 px-7 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-[#25D366] hover:text-[#25D366]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const ContactStrip = () => (
  <section className="py-20 lg:py-28" data-testid="home-contact-section">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading eyebrow="Contact & Location" title="Head Office - Vashi, Navi Mumbai." />
          <Reveal delay={0.1}>
            <address className="mt-8 not-italic">
              <p className="font-display text-2xl font-semibold text-navy">{FIRM.name}</p>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{FIRM.addressShort}</p>
            </address>
            <div className="mt-7 space-y-3">
              <a href={TEL_LINK} data-testid="home-contact-call" className="group flex items-center gap-3 text-sm font-semibold text-navy-700 transition-colors hover:text-corpblue">
                <span className="flex h-9 w-9 items-center justify-center border border-slate-200 transition-colors group-hover:border-corpblue"><Phone className="h-4 w-4" /></span>
                {FIRM.phoneDisplay}
              </a>
              <a href={`mailto:${FIRM.email}`} data-testid="home-contact-email" className="group flex items-center gap-3 break-all text-sm font-semibold text-navy-700 transition-colors hover:text-corpblue">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-slate-200 transition-colors group-hover:border-corpblue"><MessageCircle className="h-4 w-4" /></span>
                {FIRM.email}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={FIRM.mapsDirectionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-get-directions-button"
                className="inline-flex items-center gap-2 bg-navy px-5 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-corpblue"
              >
                <MapPin className="h-4 w-4" /> Get Directions
              </a>
              <Link
                to="/contact"
                data-testid="home-contact-page-link"
                className="inline-flex items-center gap-2 border border-navy px-5 py-3 text-xs font-bold uppercase tracking-widest text-navy transition-colors hover:bg-navy hover:text-white"
              >
                Contact Page
              </Link>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="h-full min-h-[360px] border border-slate-200" data-testid="home-map-embed">
            <iframe
              title="Aniruddha Sonar & Associates - Haware Fantasia Business Park, Vashi, Navi Mumbai"
              src={FIRM.mapsEmbedUrl}
              className="h-full min-h-[360px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <SEO
        title="Aniruddha Sonar & Associates | Chartered Accountants, Vashi, Navi Mumbai"
        description="Chartered Accountants in Vashi, Navi Mumbai. Income tax, GST, audit, accounting, startup advisory, NRI taxation, RERA compliance and Virtual CFO services."
        path="/"
      />
      <Hero />
      <Marquee />
      <AboutFirm />
      <FounderSpotlight />
      <CoreServices />
      <Industries />
      <TeamSection />
      <Approach />
      <InsightsPreview />
      <GoogleReviews />
      <Portals />
      <ConsultationCTA />
      <ContactStrip />
    </>
  );
}
